import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Flame, FileText, Shield, PhoneOff, CalendarClock, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_ICONS = {
  chaud_non_traite: { icon: Flame, color: '#F97316', label: 'Chaud non traité' },
  devis_sans_retour: { icon: FileText, color: '#F59E0B', label: 'Devis sans retour' },
  rdv_demain: { icon: CalendarClock, color: '#10B981', label: 'RDV demain' },
};

export default function AlertsWidget({ onProspectClick }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/alerts?resolved=false&limit=20');
      setAlerts(data.alerts || []);
    } catch {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resolve = async (id) => {
    try {
      await api.post(`/alerts/${id}/resolve`, { resolved: true });
      setAlerts(a => a.filter(x => x.id !== id));
      toast.success("Alerte résolue");
    } catch {
      toast.error("Erreur");
    }
  };

  if (loading) return null;
  if (alerts.length === 0) return null;

  return (
    <Card data-testid="alerts-widget" className="border-l-4 border-l-[#EF4444]">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
          Alertes du moment ({alerts.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2 max-h-[300px] overflow-y-auto">
        {alerts.map(a => {
          const cfg = TYPE_ICONS[a.type] || { icon: AlertTriangle, color: '#71717A', label: a.type };
          const Icon = cfg.icon;
          return (
            <div
              key={a.id}
              className="flex items-start gap-2 p-2 rounded-lg border border-[#E5E7EB] hover:bg-[#FAFAFA] transition-colors"
              data-testid={`alert-${a.id}`}
            >
              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ background: `${cfg.color}15`, color: cfg.color }}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Badge variant="outline" className="text-[10px] h-4" style={{ borderColor: cfg.color, color: cfg.color }}>
                    {cfg.label}
                  </Badge>
                  {a.severity === 'critical' && <Badge className="text-[10px] h-4 bg-[#EF4444]">Urgent</Badge>}
                </div>
                <div className="text-xs text-[#18181B] truncate">{a.message}</div>
                <div className="text-[10px] text-[#71717A] mt-0.5">
                  {new Date(a.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {onProspectClick && (
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => onProspectClick(a.prospect_id)} data-testid={`alert-open-${a.id}`}>
                    Ouvrir
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => resolve(a.id)} data-testid={`alert-resolve-${a.id}`} title="Marquer comme traitée">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
